import { forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { HeadlessButton, HeadlessButtonProps } from '../button';
import { Icon, IconDictionary } from '../icon';
import type { TooltipVariant } from '../tooltip';
import { Tooltip } from '../tooltip';
import { TooltipAlign } from '../tooltip/tooltip.component';
import { VisuallyHidden } from '../visually_hidden';
import { theme } from './icon_button.theme';

type IconButtonVariant = 'neutral' | 'interactive' | 'on-dark' | 'info' | 'success' | 'warning' | 'danger';
type IconButtonSize = 'small' | 'medium' | 'large';

// The button component does not include "cursor: pointer;" due to
// official standards specifying that: "The cursor is a pointer that indicates
// a link." https://www.w3.org/TR/CSS21/ui.html#propdef-cursor
//
// Microsoft and Apple also specifies that the "correct" interaction is to
// reserve the pointing hand to URL links
// https://docs.microsoft.com/en-gb/windows/win32/uxguide/inter-mouse?redirectedfrom=MSDN
// https://developer.apple.com/design/human-interface-guidelines/macos/user-interaction/mouse-and-trackpad/
//
// If you'd want to have buttons with cursors, that's fine, but you'll need to
// extend the button component in order to get that interaction
const StyledIconButton = styled(HeadlessButton)`
	--text-color: inherit;
	--hover-background: ${theme.getValue('baseHoverBackground')};
	--active-background: ${theme.getValue('baseActiveBackground')};
	--padding: 0;
	--iconSize: 0;

	user-select: none;
	-webkit-tap-highlight-color: transparent;
	-webkit-user-drag: none;
	-webkit-touch-callout: none;

	border: none;
	margin: 0;
	width: auto;
	overflow: visible;
	line-height: normal;
	-webkit-font-smoothing: inherit;
	-moz-osx-font-smoothing: inherit;
	-webkit-appearance: none;
	appearance: none;
	transition: background 150ms ease-in;
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	outline: none;
	word-wrap: break-word;
	word-break: break-word;
	background: none;
	border-radius: ${theme.getValue('baseBorderRadius')};
	font: ${theme.getValue('baseText')};
	padding: var(--padding);
	color: var(--text-color);

	& > svg {
		width: var(--iconSize);
		height: var(--iconSize);
	}

	&:focus-visible {
		outline: 2px solid ${theme.getValue('baseFocusRingColor')};
		outline-offset: 2px;
	}

	// Variants
	&[data-variant='interactive'] {
		--text-color: ${theme.getValue('interactiveTextColor')};
		--hover-background: ${theme.getValue('interactiveHoverBackground')};
		--active-background: ${theme.getValue('interactiveActiveBackground')};
	}
	&[data-variant='on-dark'] {
		--text-color: ${theme.getValue('onDarkTextColor')};
		--hover-background: ${theme.getValue('onDarkHoverBackground')};
		--active-background: ${theme.getValue('onDarkActiveBackground')};
	}
	&[data-variant='info'] {
		--text-color: ${theme.getValue('infoTextColor')};
		--hover-background: ${theme.getValue('infoHoverBackground')};
		--active-background: ${theme.getValue('infoActiveBackground')};
	}
	&[data-variant='success'] {
		--text-color: ${theme.getValue('successTextColor')};
		--hover-background: ${theme.getValue('successHoverBackground')};
		--active-background: ${theme.getValue('successActiveBackground')};
	}
	&[data-variant='warning'] {
		--text-color: ${theme.getValue('warningTextColor')};
		--hover-background: ${theme.getValue('warningHoverBackground')};
		--active-background: ${theme.getValue('warningActiveBackground')};
	}
	&[data-variant='danger'] {
		--text-color: ${theme.getValue('dangerTextColor')};
		--hover-background: ${theme.getValue('dangerHoverBackground')};
		--active-background: ${theme.getValue('dangerActiveBackground')};
	}

	// Sizes
	&[data-size='small'] {
		--padding: ${theme.getValue('smallPadding')};
		--iconSize: ${theme.getValue('smallIconSize')};
	}
	&[data-size='medium'] {
		--padding: ${theme.getValue('mediumPadding')};
		--iconSize: ${theme.getValue('mediumIconSize')};
	}
	&[data-size='large'] {
		--padding: ${theme.getValue('largePadding')};
		--iconSize: ${theme.getValue('largeIconSize')};
	}

	&[aria-busy='true'] {
		opacity: 0.6;
		cursor: progress;
	}

	&[aria-disabled='true'] {
		opacity: 0.6;
		cursor: not-allowed;
	}

	@media (hover: hover) {
		&:not([aria-busy='true'], [aria-disabled='true']) {
			&:active {
				background: var(--active-background);
				transition: none;
			}

			&:hover {
				background: var(--hover-background);
			}
		}
	}
`;

interface IconButtonProps extends HeadlessButtonProps {
	/**
	 * The visual presentation of the icon button.
	 */
	variant: IconButtonVariant;

	/**
	 * The visual size of the icon button.
	 */
	size: IconButtonSize;

	/**
	 * Specifies the icon to use in the button.
	 */
	icon: keyof IconDictionary;

	/**
	 * A description of the action of the icon button.
	 */
	text: string;

	/**
	 * The visual representation of the tooltip.
	 * @default dark
	 */
	tooltipVariant?: TooltipVariant;

	tooltipAlign?: TooltipAlign;
	loading?: boolean;
}

function CellulaIconButton(
	{ icon, variant, size, text, tooltipAlign = 'center', tooltipVariant = 'dark', loading, ...rest }: IconButtonProps,
	ref: Ref<HTMLButtonElement>,
): JSX.Element {
	return (
		<Tooltip content={text} variant={tooltipVariant} align={tooltipAlign}>
			<StyledIconButton data-variant={variant || undefined} data-size={size || undefined} busy={loading} {...rest} ref={ref}>
				<VisuallyHidden>{text}</VisuallyHidden>
				<Icon type={icon} />
			</StyledIconButton>
		</Tooltip>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A slim component for allowing users to preform certain actions.
 */
const IconButton = forwardRef(CellulaIconButton);

export type { IconButtonProps };
export { IconButton };
